import {
  DEVICE_PLATFORMS,
  DIET_TYPES,
  PLAN_STATUSES,
  type AdminUserListItem,
} from '@steado/admin-contract/users';
import { useQuery } from '@tanstack/react-query';
import { Ban, ChevronRight } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';

import { ErrorState } from '@/components/shell/ErrorState';
import { DataTable, type TableColumn, type TableRow } from '@/components/table/DataTable';
import { PaginationFooter } from '@/components/table/PaginationFooter';
import { ScopePills } from '@/components/table/ScopePills';
import { TableToolbar, type FilterSpec } from '@/components/table/TableToolbar';
import { BadgeCell, PersonCell, TextCell } from '@/components/table/cells';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth/auth-context';
import { scopeCountsQueryOptions } from '@/lib/scope-counts-query';
import { planTone } from '@/lib/plan-tone';
import { gymDetailQueryOptions } from '@/screens/gym-detail/gym-detail-query';

import { USERS_PAGE_SIZE, usersQueryOptions, type UsersFilters } from './users-query';
import { USER_SCOPES, filtersForScope, scopeForFilters, type UserScopeId } from './users-scopes';

const COLUMNS: readonly TableColumn[] = [
  { key: 'person', label: 'Person', width: '1.6fr' },
  { key: 'joined', label: 'Joined', width: '0.95fr' },
  { key: 'goal', label: 'Goal and diet', width: '1.15fr' },
  { key: 'kcal', label: 'Daily kcal', width: '0.8fr', align: 'right' },
  { key: 'plan', label: 'Plan', width: '0.75fr' },
  { key: 'access', label: 'Access', width: '0.95fr' },
  { key: 'open', label: '', width: '44px', align: 'right' },
];

const PLATFORM_LABELS = { ios: 'iOS', android: 'Android', web: 'Web' } as const;

/** Lookup rather than a scan: this runs once per row, per render. */
const LABELS = new Map<string, string>([
  ...DIET_TYPES.map((diet) => [diet, diet.replace(/_/g, '-')] as const),
  ...PLAN_STATUSES.map((status) => [status, status] as const),
]);

function humanise(value: string): string {
  return LABELS.get(value) ?? value.replace(/_/g, ' ');
}

const DATE_FORMAT = new Intl.DateTimeFormat('en-IN', {
  day: '2-digit',
  month: 'short',
  year: '2-digit',
});

function initialsOf(user: AdminUserListItem): string {
  const name = user.displayName?.trim() ?? '';
  if (name === '') return '??';

  const parts = name.split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return `${first}${last}`.toUpperCase();
}

export interface UsersScreenProps {
  readonly filters: UsersFilters;
  readonly onFiltersChange: (next: UsersFilters) => void;
  /** Opens the detail screen. A row is a button, so this works from the keyboard. */
  readonly onOpenUser: (userId: string) => void;
}

export function UsersScreen({ filters, onFiltersChange, onOpenUser }: UsersScreenProps) {
  const { api } = useAuth();

  /**
   * The API hands back only a forward cursor, so walking back needs the
   * cursors already visited. Filters live in the URL — that is the shareable
   * part — while the page position is session state.
   */
  const [cursorStack, setCursorStack] = useState<(string | undefined)[]>([]);

  const query = useQuery(usersQueryOptions(api, filters));
  /*
   * Already in the cache by the time this mounts — the shell fetches it for
   * the sidebar badges. A pill renders without its number if it is not, which
   * is why `Scope.count` is optional.
   */
  const counts = useQuery(scopeCountsQueryOptions(api));
  /*
   * A gym's id arrives from its page ("Open the coached members in Users"). Its
   * name comes from that page's own query, usually already cached, so the
   * dropdown and the title can name the gym instead of showing an id.
   */
  const gymId =
    filters.gym === undefined || filters.gym === 'any' || filters.gym === 'none' ? undefined : filters.gym;
  const gym = useQuery({ ...gymDetailQueryOptions(api, gymId ?? ''), enabled: gymId !== undefined });
  const gymName = gymId === undefined ? undefined : (gym.data?.name ?? 'one gym');

  const setFilter = useCallback(
    (patch: Partial<UsersFilters>) => {
      // Any filter change invalidates the paging position.
      setCursorStack([]);
      onFiltersChange({ ...filters, ...patch, cursor: undefined });
    },
    [filters, onFiltersChange],
  );

  const onNext = useCallback(() => {
    const next = query.data?.pagination.nextCursor;
    if (next === undefined || next === null) return;
    setCursorStack((stack) => [...stack, filters.cursor]);
    onFiltersChange({ ...filters, cursor: next });
  }, [query.data, filters, onFiltersChange]);

  const onPrevious = useCallback(() => {
    setCursorStack((stack) => {
      const previous = stack[stack.length - 1];
      onFiltersChange({ ...filters, cursor: previous });
      return stack.slice(0, -1);
    });
  }, [filters, onFiltersChange]);

  const onReset = useCallback(() => {
    setCursorStack([]);
    onFiltersChange(filtersForScope(scopeForFilters(filters), {}));
  }, [filters, onFiltersChange]);

  /*
   * Two dropdowns, not five: status and plan are the scope pills above, and
   * offering the same filter twice is two controls that can disagree.
   */
  const filterSpecs: readonly FilterSpec[] = useMemo(
    () => [
      {
        id: 'diet',
        label: 'Diet',
        value: filters.diet,
        options: DIET_TYPES.map((diet) => ({ value: diet, label: humanise(diet) })),
        onChange: (diet) => {
          setFilter({ diet });
        },
      },
      {
        id: 'platform',
        label: 'Platform',
        value: filters.platform,
        options: DEVICE_PLATFORMS.map((platform) => ({ value: platform, label: PLATFORM_LABELS[platform] })),
        onChange: (platform) => {
          setFilter({ platform });
        },
      },
      {
        id: 'gym',
        label: 'Gym',
        value: filters.gym,
        // Coached now, meaning an active membership: someone who left a gym is "not in a gym".
        options: [
          { value: 'any', label: 'In a gym' },
          { value: 'none', label: 'Not in a gym' },
          ...(gymId === undefined ? [] : [{ value: gymId, label: gymName ?? 'one gym' }]),
        ],
        onChange: (value) => {
          setFilter({ gym: value });
        },
      },
    ],
    [filters.diet, filters.platform, filters.gym, gymId, gymName, setFilter],
  );

  const rows: readonly TableRow[] = useMemo(
    () =>
      (query.data?.items ?? []).map((user) => ({
        id: user.id,
        onOpen: () => {
          onOpenUser(user.id);
        },
        cells: [
          <PersonCell key="person" name={user.displayName} sub={user.phone} initials={initialsOf(user)} />,
          <TextCell
            key="joined"
            value={DATE_FORMAT.format(new Date(user.createdAt))}
            /*
             * Two dates in one cell because the gap between them is the
             * interesting part: an account that signed up in March and
             * finished onboarding in July is a different story from one that
             * did both on the same afternoon, and one that never finished is
             * the biggest cohort on this screen.
             */
            sub={
              user.onboardedAt === null
                ? 'never onboarded'
                : `onboarded ${DATE_FORMAT.format(new Date(user.onboardedAt))}`
            }
          />,
          <TextCell
            key="goal"
            value={user.goals.length === 0 ? null : humanise(user.goals[0] ?? '')}
            sub={user.diet === null ? null : humanise(user.diet)}
          />,
          <TextCell
            key="kcal"
            value={user.targets === null ? null : String(user.targets.kcal)}
            mono
            align="right"
          />,
          user.planStatus === null ? (
            <TextCell key="plan" value={null} />
          ) : (
            <BadgeCell key="plan" label={user.planStatus} tone={planTone(user.planStatus)} dot />
          ),
          <span key="access" className="flex min-w-0 items-center gap-1.5">
            <BadgeCell label={user.entitlement} tone={user.entitlement === 'premium' ? 'accent' : 'mute'} />
            {user.platform === null ? null : <BadgeCell label={PLATFORM_LABELS[user.platform]} tone="mute" />}
            {user.status === 'deactivated' ? (
              <Ban aria-label="Deactivated" className="size-3.5 shrink-0 text-err-fg" />
            ) : null}
          </span>,
          <span key="open" className="flex justify-end">
            <ChevronRight aria-hidden="true" className="size-4 text-input" />
          </span>,
        ],
      })),
    [query.data, onOpenUser],
  );

  const scope = scopeForFilters(filters);
  /*
   * "Filtered" means the toolbar, not the scope: a pill is where you are, and
   * offering to clear it as though it were a stray filter would make the
   * default view unreachable from five of the six scopes.
   */
  const isFiltered =
    filters.search !== undefined ||
    filters.diet !== undefined ||
    filters.platform !== undefined ||
    filters.gym !== undefined;

  const pageOffset = cursorStack.length * USERS_PAGE_SIZE;

  if (query.isError) {
    return (
      <ErrorState
        title="Could not load users"
        message={query.error.message}
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  }

  return (
    <div className="flex max-w-[1240px] flex-col gap-3">
      <ScopePills
        label="User scope"
        scopes={USER_SCOPES.map((scope) => ({
          id: scope.id,
          label: scope.label,
          ...(counts.data === undefined ? {} : { count: counts.data.users[scope.countKey] }),
        }))}
        value={scope}
        onChange={(next: UserScopeId) => {
          setCursorStack([]);
          onFiltersChange(filtersForScope(next, filters));
        }}
      />

      <TableToolbar
        searchValue={filters.search ?? ''}
        searchPlaceholder="Name or phone"
        onSearchChange={(search) => {
          setFilter({ search: search === '' ? undefined : search });
        }}
        filters={filterSpecs}
        isFiltered={isFiltered}
        onReset={onReset}
      />

      <DataTable
        caption="Users"
        title={
          gymName === undefined
            ? (USER_SCOPES.find((candidate) => candidate.id === scope)?.label ?? 'Everyone')
            : `Coached at ${gymName}`
        }
        count={rows.length === 0 ? undefined : `showing ${String(rows.length)}`}
        columns={COLUMNS}
        rows={rows}
        isLoading={query.isPending}
        emptyTitle={isFiltered ? 'No matches' : 'No users yet'}
        emptyBody={
          isFiltered
            ? 'No person matches the current search and filters. Clear them to see everyone in this scope.'
            : 'Users appear here as soon as they sign up, whether or not onboarding is finished.'
        }
        emptyAction={
          isFiltered ? (
            <Button variant="outline" size="sm" onClick={onReset}>
              Clear filters
            </Button>
          ) : undefined
        }
        footer={
          <PaginationFooter
            rangeStart={rows.length === 0 ? 0 : pageOffset + 1}
            rangeEnd={pageOffset + rows.length}
            hasPrevious={cursorStack.length > 0}
            hasNext={query.data?.pagination.hasMore ?? false}
            onPrevious={onPrevious}
            onNext={onNext}
            isLoading={query.isPending}
          />
        }
      />
    </div>
  );
}
